import { UtcDateString } from '../common'
import { PlanCalculationAlgorithm } from './plan-service.types'

/**
 * Интерфейс: Исходные данные версии плана (сырые данные)
 * 
 * {@link https://apsmgmtapi.docs.apiary.io/#reference/0/2/2 API: Aps Mgmtapi} 
 * 
 * @interface IRawData 
 */
export type IRawData = {

    /** Настройки расчета */
    calculationSettings: ICalculationSettings,

    /** Технологии */
    technologies: IHashedObject<ITechnology>[],

    /** Заказы */
    orders: IHashedObject<IRawOrder>[],

    /** Заказы */
    productionOrders: IHashedObject<IRawProductionOrder>[],

    /** НЗП */
    wip?: IIntegrationObject<IUncompletedDetail>,

    /** Места хранения */
    warehouse: IWarehouse[],

    /** календари */
    calendar: ICalendar[],

    /** рабочие места */
    workplaces: IHashedObject<IWorkplace>[],

    /** готовая продукция */
    endProducts?: IIntegrationObject<IEndProducts>,

    /** план поставки материалов */
    deliveryPlan?: IIntegrationObject<IDeliveryPlan>,

    /** матрица перемещений*/
    transitionMatrix?: ITransitions,

    /** веса оптимизации */
    weight?: IWeight[],

    /** сотрудники*/
    staff?: IStaff[],

    /** директивы */
    directive?: IDirectives,

    rawDataChangedAtUtc?: UtcDateString,

    businessCalendar?: BusinessCalendar[],

    materials?: Material[],
}

/** Справочник материалов */
export type Material = {
    id: string,
    n: string,
    mu: string,
    p: number,
    toWh?: boolean,
    isolated?: boolean,
    pr?: number,
    batch?: number,
    period?: number,
    dim?: {
        length?: number,
        height?: number,
        width?: number,
        mu: string,
    },
    mass?: {
        m: number,
        mu: string,
    },
} & IEntityId

export type BusinessCalendar = {
    id: string,
    holidays: Holiday[],
} & IEntityId

export type Holiday = {
    date: string,
    type: string,
}

export enum MaterialPlanMode {
    UseDelivery = 'useDelivery',
    CreateRequirement = 'createRequirement',
    Disabled = 'disabled',
}

export enum AssemblyMovementMode {
    Enable = 'enable',
    Disable = 'disable',
}

/**
 * Интерфейс: Настройки расчета
 * 
 */
export type ICalculationSettings = {

    /** Дата начала работ*/
    startFrom: string,

    /** Дата фактических остатков*/
    factDate: string,

    /** План поставки материалов */
    materialPlanMode: MaterialPlanMode,

    /** Перемещение производимых материалов */
    assemblyMovementMode: AssemblyMovementMode,

    /** Перемещение покупных материалов */
    materialMovementMode: string,

    /** Потребность в персонале */
    personnelReportMode: string,

    /** Тип операции */
    calculationType: PlanCalculationAlgorithm,

    /** Режим расчёта персонала */
    staffRequirementMode: number,

    /** Валюта */
    currency: CurrencyCode,
}

export enum CurrencyCode {
    Ruble = 'RUB',
    // Euro = 'EUR',
}

export type IHashedObject<T> = {

    /** Хэш */
    hash: string,

    /** Данные */
    data: T,
}

export type IIntegrationObject<T> = {

    /** Дата последнего изменения */
    dt: string,

    /** Данные */
    data: T[],

}

export type IEntityId = {
    /** Идентификатор сущности в черновике */
    eId?: number,
}

/**
 * Интерфейс: Технология
 */
export type ITechnology = {

    /** Артикул материала */
    id: string,

    /** Название материала */
    n: string,

    /** список вариантов изготовления (ВИМ) */
    rc: IRouteCode[],

} & IEntityId;

/**
 * Интерфейс: ВИМ
 */
export type IRouteCode = {

    /** Наименование ВИМ */
    id: string,

    /** список точек маршрута */
    rp: IRoutePoint[],

}

/**
 * Интерфейс: Точка маршрута
 */
export type IRoutePoint = {

    /** Код точки маршрута */
    id: string,

    /** Цех.участок */
    ms: string,

    /** список операций */
    os: IOperation[],
}

/**
 * Интерфейс: Операция
 */
export type IOperation = {

    /** код операции */
    id: string,

    /** наименование */
    n: string,

    /** код группы взаимозаменяемости */
    ot: string,

    /** нормо-час */
    tn: number,

    /** станко-час */
    tw: number,

    /** стоимость */
    cp: number,

    /** специальность рабочего */
    wo?: string,

    /** выходня е.и. корневой ДСЕ */
    mu: string,

    /** разряд рабочего */
    wr?: number,

    /** список входящих компонентов */
    ds?: IIncomingMaterial[],

    /** компетенция */
    sr?: string,
}

/**
 * Интерфейс: Входящий материал
 */
export type IIncomingMaterial = {

    /** Артикул */
    id: string,

    /** Наименование */
    n: string,

    /** Количество */
    c: number,

    /** Единица измерения */
    mu: string,
}

/**
 * Режим расчета
 */
export enum CalculationMode {
    /** вперёд */
    Forward = 'forward',

    /** назад */
    Back = 'back',
}

/**
 * Интерфейс: Заказ
 */
export type IRawOrder = {

    /** Номер */
    id: string,

    /** артикул ДСЕ */
    d: string,

    /** Количество */
    c: number,

    /** приоритет */
    p: number,

    /** единица измерения */
    mu: string,

    /** режим расчета */
    calculationMode: CalculationMode,

    /** дата начала работ */
    bgn?: string,

    /** планируемая дата отгрузки */
    exp?: string,

    /** имя клиента */
    cn?: string,

    /** статус заказа */
    s: string,

    /** зарезервировать до */
    rd?: string,

    /** комментарий */
    cm?: string,

    /** количество сделано по заказу */
    rc?: number,

    /** список тегов заказа */
    t?: string[],

    /** потери по заказу */
    lc?: number,

    /** родители производственного заказа */
    parent?: string[]
} & IEntityId;

/**
 * Интерфейс: Производственный заказ
 */
export type IRawProductionOrder = {
    /** номер клинтского заказа */
    cId?: string,

    /** группа производства */
    group?: string,
} & IRawOrder

/**
 * Интерфейс: НЗП
 */
export type IUncompletedDetail = {

    /** id */
    id: string,

    /** код операции
    */
    op: string,

    /** подтвержденное количество */
    c: number,

    /** код места хранения */
    w?: string,

    /** стоимость */
    p: number,

    /** дата изготовления */
    do?: string,

    /** номер заказа
     * @deprecated использовать o вместо on
    */
    on?: string,

    /** номер заказа */
    o?: string,

    /** номер рабочего места */
    wp?: string,

    /** количество, которое нужно доделать */
    cu?: number,

    /** режим потребления */
    cm?: ConsumptionMode,
} & IEntityId;

export enum ConsumptionMode {
    Partial = 'partial',
    Batch = 'batch',
}


/**
 * Интерфейс: Склад
 */
export type IWarehouse = {

    /** код места хранения */
    id: string,

    /** наименование */
    n: string,

    /** цех.участок */
    ms: string,

    /** список хранящихся материалов */
    ds: IWarehouseMaterial[],
} & IEntityId;

/**
 * Интерфейс: Материал на складе
 */
export type IWarehouseMaterial = {

    /** артикул ДСЕ */
    d: string,

    /** наименование */
    n: string,

    /** вместимость */
    c?: number,
}


/**
 * Интерфейс: Календарь
 */
export type ICalendar = {

    /** наименование */
    id: string,
    n?: string,

    bc?: string,

    /** периоды календаря */
    p: ICalendarPeriod[],
}

/**
 * Интерфейс: Периоды календаря
 */
export type ICalendarPeriod = {

    /** начало периода */
    ps: string,

    /** конец периода */
    pe: string,

    /** смены */
    s?: ICalendarShift[],
}

/**
 * Интерфейс: Смена
 */
export type ICalendarShift = {

    /** id смены */
    id?: number,

    /** начало смены (процент от 24 часов) */
    ss: number,

    /** конец смены (процент от 24 часов) */
    se: number,

    /** начало перерыва (процент от 24 часов) */
    bs?: number,

    /** конец перерыва (процент от 24 часов) */
    be?: number,

    /** допустимое время превышения в секундах */
    e?: number,

    /** исключенные дни */
    ex?: string,

    /** флаги работы по дням недели */
    wd?: number[],

    b?: CalendarBreak[],
}

export type CalendarBreak = {
    /** начало перерыва (процент от 24 часов) */
    bs: number,

    /** конец перерыва (процент от 24 часов) */
    be: number,
}

/**
 * Интерфейс: Рабочее место
 */
export type IWorkplace = {

    /** Порядковый номер */
    seqId?: number,

    /** код */
    id: string,

    /** модель */
    m: string,

    /** группа оборудования */
    wg: string,

    /** цех.участок */
    ms: string,

    /** группы взаимозаменяемости */
    t: string[],

    isBlackBox?: boolean,
} & IEntityId;

/**
 * Интерфейс: Готовая продукция
 */
export type IEndProducts = {

    /** id */
    id: string,

    /** номер заказа */
    o?: string,

    /** артикул ДСЕ */
    d: string,

    /** дата выпуска */
    do: string,

    /** количество */
    c: number,

    /** единица измерения */
    mu: string,

    /** номер партии */
    bn: number,

    /** стоимость */
    p: number,

    /** место хранения */
    w: string,
} & IEntityId;

/**
 * Интерфейс: План поставки материалов
 */
export type IDeliveryPlan = {

    id: string,

    /** артикул ДСЕ */
    d: string,

    /** номер заказа */
    o?: string,

    /** наименование */
    n?: string,

    /** код места хранения */
    wh: string,

    /** единица измерения */
    mu: string,

    /** дата поставки */
    dt: string,

    /** количество */
    c: number,

    /** номер партии */
    bn: number,

    /** стоимость партии */
    bc: number,
} & IEntityId

/**
 * Интерфейс: Веса оптимизации
 */
export type IWeight = {

    /** номер заказа */
    o: string,

    /** артикул ДСЕ */
    d: string,

    /** код ВИМ */
    mo: string,

    /** код точки маршрута */
    rp?: string,

    /** код операции */
    op?: string,

    /** список весов оптимизации (4 числа) */
    w: number[],
} & IEntityId

/**
 * Интерфейс: Люди
 */
export type IStaff = {

    /** табельный номер */
    id: string,

    /** фамилия */
    sn?: string,

    /** имя */
    n?: string,

    /** отчество */
    mn?: string,

    /** календарь */
    c: string,

    /** специальность */
    sp?: {
        r?: number,
        n: string,
    }[],

    /** компетенции */
    sr?: string[],

    ms?: string,

    g?: string,
} & IEntityId;

/**
 * Интерфейс: Перемещения
 */
export type ITransitions = {

    /** матрица */
    matrix: ITransitionMatrix[],

    /** список групп */
    group?: ITransitionGroup[],
}

/**
 * Интерфейс: Матрица перемщений
 */
export type ITransitionMatrix = {

    /** откуда */
    from: ITransitionPlace,

    /** куда */
    to: ITransitionPlace,

    /** время перемещения */
    t: string,
} & IEntityId

/**
 * Интерфейс: Место перемещения
 */
export type ITransitionPlace = {

    /** группа */
    g?: string,

    /** цех */
    m?: string,

    /** участок */
    s?: string,

    /** место хранения */
    wh?: string,

    /** код рабочего места */
    wp?: string,
}

/**
 * Интерфейс: Группа перемещения
 */
export type ITransitionGroup = {
    /** код группы */
    g: string,

    /** список элементов группы */
    i: ITransitionPlace[],
} & IEntityId

/**
 * Интерфейс: Директивы
 */
export type IDirectives = {
    /** директивы по технологии
     * @deprecated использовать новые директивы
    */
    technology: IDirectiveTechnology[],

    /** директивы по технологии */
    workMode: IWorkMode[],

    /** привязка рабочего места и операции */
    workplaceOperationBinding: IWorkplaceOperationBinding[],

    /** время наладки для групп оборудования */
    workplaceAdjustment: IWorkplaceAdjustment[],

    /** настройки рабочих мест */
    workplaceSettings: IWorkplaceSetting[],

    /** альтернативные материалы */
    altMaterials: IAlternativeMaterials[],

    /** минимальная партия материала */
    materialMin: IMaterialMin[],

    /** материалы с двойными единицами измерения */
    materialMeasureUnits: IMaterialMeasureUnits[],

    /** материалы на склад */
    materialToWarehouse: IMaterialToWarehouse[],

    /** комплектные операции */
    kit: ICompleteOperation[],

    /** Чёрный ящик */
    blackBox: IBlackBox[],

    /** Альтернативный ВИМ */
    altRoute: IAltRoute[],

    /** Ограничение размера партии изготовления */
    batchSize: IBatchSize[],

    /** Производительность непрерывных операций */
    workplacePerformances: IWorkplacePerformancesV2[],

    /** Управление прерываниями операций */
    operationBreak: IOperationBreak[],

    /** Садочные операции */
    basketOperations: IBasketOperations[],

    /** Одновременные операции */
    simultaneousOperations: SimultaneousOperations[],

    /** Процент выполнения операции */
    percentExecution: IPercentExecutionOperations[],

    /** Управление временем наладки */
    adjustmentControl: IAdjustmentControlV2[],

    /** Выполнение следующей операции на том же рабочем месте */
    consistentOperations: IConsistentOperations[],

    /** Управление режимом работы персонала */
    staffWorkMode: StaffWorkMode[],

    /** Размеры кампаний */
    campaign: CampaignSizes[],

    /** Стратегии */
    strategies: Strategies[],

    /** Параметры стратегий */
    strategyParams: StrategyParams[],

    /** Параметры стратегий для рабочих мест */
    strategyWorkplaceParams: StrategyWorkplaceParams[],

    /** Особые ограничения рабочих мест */
    workplaceRestrictions: WorkplaceRestrictions[],
}

export type WorkplaceRestrictions = {
    restriction: RestrictionType,
    value: number,
    mu?: string,
    gr?: string,
    workplace: string,
} & IEntityId;

export enum RestrictionType {
    Length = 'length',
    MultitaskingStaff = 'multitasking-staff',
}

export enum StrategyWorkplaceParameters {
    LoadPercent = 'loadPercent',
    LoadWindow = 'loadWindow',
}

export type StrategyWorkplaceParams = {
    workplace: string,
    t?: string,
    strategy: StrategyType,
    key: StrategyWorkplaceParameters,
    value: string,
} & IEntityId;

export enum StrategyType {
    Campaign = 'campaign',
    WorkRetention = 'workRetention',
    BatchUnion = 'batchUnion',
    SimultaneousPacking = 'simultaneousPacking',
}

export enum StrategyParameters {
    AdjPenalty = 'adjPenalty',
    BatchUnion = 'batchUnion',
}

export type StrategyParams = {
    t?: string,
    strategy: StrategyType,
    key: StrategyParameters,
    value: string,
} & IEntityId;

export type Strategies = {
    s: StrategyType[],
    t?: string,
} & IEntityId;

export type CampaignSizes = {
    op: string,
    t?: string,
    c: number,
} & IEntityId;

export type StaffWorkMode = {
    id: string,
    ex: StaffWorkModeUnavailablePeriod[],
} & IEntityId;

export type StaffWorkModeUnavailablePeriod = {
    s: string,
    e: string,
    type: string,
}

export type IConsistentOperations = {
    op: string,
    isWpLocked?: boolean,
} & IEntityId;

/**
 * Интерфейс: Управление временем наладки
 */
export type IAdjustmentControlV2 = {
    /** код операции */
    op: string,
} & IAdjustmentControl

/**
 * Интерфейс: Садочные операции
 */
export type IBasketOperations = {
    /** группа взаимозаменяемости */
    gn: string,
    gr: {
        op: string,
        v: number,
    }[],
} & IEntityId;

/**
 * Интерфейс: Одновременные операции
 */
export type SimultaneousOperations = {
    /** код операции */
    op: string,
    /** количество */
    v: number,
} & IEntityId;

/**
 * Интерфейс: Производительность непрерывных операций
 */
export type IWorkplacePerformancesV2 = {
    /** код операции */
    op: string,
} & IWorkplacePerformance

/**
 * Интерфейс: Ограничение размера партии изготовления
 */
export type IBatchSize = {
    /** артикул материала */
    d: string,

    /** ВИМ */
    rc?: string,

    /** точка маршрута */
    rp?: string,

    /** номер операции */
    op?: string,

    p?: boolean,

    /** порог укрупнения */
    u?: number,
} & IBatchSizeLimitation

/**
 * Интерфейс: Альтернативный ВИМ
 */
export type IAltRoute = {
    /** артикул материала */
    d: string,

    /** ВИМ */
    rc: string,

    o: {
        o: string,
        eId: number,
    }[],
} & IEntityId

/**
 * Интерфейс: Чёрный ящик, виртуальные операции
 */
export type IBlackBox = {
    /** календарь */
    cal: string,

    /** цех и участок */
    ms: string,

    /** группа взаимозаменяемости */
    gn: string,
    gr: IVirtualOperationsV2[],

    /** бесконечная вместимость */
    ic?: boolean,
} & IEntityId

/**
 * Интерфейс: Виртуальные операции V2
 */
export type IVirtualOperationsV2 = {
    d: string,

    /** стоимость */
    p: number,

    /** производимость в час
     * @deprecated использовать directive.gr.pt - время обработки
    */
    ph?: number,

    /** время обработки */
    pt?: number,

    /** ВИМ */
    rc?: string,

    /** Точка маршрута */
    rp?: string,

    /** Тип черного ящика */
    type: string,

    /** Номер операции */
    op?: string,

    /** Такт перемещения */
    outTact?: number,
}

/**
 * Интерфейс: Комплетные операции
 */
export type ICompleteOperation = {
    kitId: string,

    ops: IOperationData[],
} & IEntityId

export type IMaterialToWarehouse = {
    detail: string,
} & IEntityId

/**
 * Интерфейс: Подробная информация по комплектным операциям
 */
export type IOperationData = {
    // Количество
    c: number,

    // Код операции
    op: string,
}

/**
 * Интерфейс: Директивы техпроцессов
 */
export type IDirectiveTechnology = {
    /** ограничение размера партии
     * @deprecated использовать directive.batchSize
     */
    bs: IBatchSizeLimitation,

    /** диретивы уровня ВИМ */
    rc: IDirectiveRouteCode[],

    id: string | number,
}

/**
 * Интерфейс: Диретивы уровня ВИМ
 */
export type IDirectiveRouteCode = {
    /** ограничение размера партии
     * @deprecated использовать directive.batchSize
     */
    bs: IBatchSizeLimitation,

    /** минимальный процент использования ВИМ
     * @deprecated использовать directive.altRoute
     */
    mpu: IRouteCodeUsage[],

    /** директивы уровня точки маршрута */
    rp: IDirectiveRoutePoint[],

    id: string | number,
}

/**
 * Интерфейс: Директивы уровня точки маршрута
 */
export type IDirectiveRoutePoint = {
    /** ограничение размера партии
     * @deprecated использовать directive.batchSize
     */
    bs: IBatchSizeLimitation,

    /** управление количеством производимых материалов в день
     * @deprecated использовать directive.blackBox.gr
     */
    dc: IVirtualOperations,

    /** директивы уровня операций */
    os: IDirectiveOperation[],

    id: string | number,
}

/**
 * Интерфейс: Директивы уровня операций
 */
export type IDirectiveOperation = {
    /** ограничение размера партии
     * @deprecated использовать directive.batchSize
     */
    bs: IBatchSizeLimitation,

    /**  управление прерыванием операции
     * @deprecated использовать directive.operationBreak
     */
    opb: IEntityId,

    /** производительность непрерывных операций
     * @deprecated использовать directive.workplacePerformances
     */
    pp: IWorkplacePerformance[],

    /** параллельные операции
     * @deprecated использовать directive.groupOperations.gr
     */
    gr: IParallelOperations,

    /** процент выполнения операции
     * @deprecated использовать directive.percentExecution
     */
    po: IPercentExecutionOperations,

    id: string | number,

    /** управление временем наладки
     * @deprecated использовать directive.adjustmentControl
     */
    ac?: IAdjustmentControl[],
}

/**
 * Интерфейс: Директива календари недоступности
 */
export type IUnavailableCalendar = {
    /** Каленадрь */
    id: string,
    name: string,

    /** список элементов группы */
    periods: {
        /** Начало вермени недоступности */
        start: string,
        /** Конец времени недоступности */
        end: string,
    }[],
}

/**
 * Интерфейс: Директива минимальная партия материала
 */
export type IMaterialMin = {
    /** артикул ДСЕ */
    d: string,

    /** единица измерения */
    mu: string,

    /** минимальная партия */
    mp: number,
} & IEntityId

/**
 * Интерфейс: Директива альтернативные материалы
 */
export type IAlternativeMaterials = {

    /** артикул материала */
    d?: string,

    /** код операции */
    t?: string,

    /** наименование группы замены */
    g: string,

    /** приоритет */
    p: number,

    /** флаг является ли тип замены эксклюзивным */
    exc: boolean,

    /** наименование календаря */
    c: string,

    /** артикул заменяемого материала */
    mat1: string,

    /** норма заменяемого материала */
    norm1: number,

    /** Е.И. заменяемого материала */
    mu1: string,

    /** артикул материала аналога */
    mat2: string,

    /** норма материала аналога */
    norm2: number,

    /** Е.И. материала аналога */
    mu2: string,
} & IEntityId

/**
 * Интерфейс: Директива настройки рабочих мест
 */
export type IWorkplaceSetting = {

    /** артикул рабочего места */
    id: string,

    /** группа оборудования */
    g: string,

    /** выходной буфер */
    buf: number,

    /** единица измерения */
    mu: string,

    /** стоимость */
    p: number,

    /** календарь */
    cal: string,

    /** календарь недоступности */
    unavailableCalendar?: string,

    /** такт перемещения */
    outTact?: number,

    /** тип раб. места */
    type: WorkplaceType,

    /** максимально допустимый процент использования оборудования в сутки */
    oee?: number,
} & IEntityId

export enum WorkplaceType {
    /** Дискретные операции */
    Discrete = 'discrete',
    /** Заглушка для труб */
    Campaign = 'campaign',
    /** Конвеер */
    Conveyor = 'conveyor',
    /** Паралельные / одновеременные операции (Печи, корзины) */
    Simultaneous = 'simultaneous',
    /** Непрерывные операции (Химики)  */
    Continuous = 'continuous',
    /** Комплектные операции  */
    Kit = 'kit',
}

type TypeSpec = {
    code: string,
    name: string,
}

export const WorkplaceTypeSpec: { [key in WorkplaceType]: Omit<TypeSpec, 'code'> } = {
    [WorkplaceType.Discrete]: { name: 'Дискретные' },
    [WorkplaceType.Campaign]: { name: 'По кампаниям' },
    [WorkplaceType.Conveyor]: { name: 'Конвейер' },
    [WorkplaceType.Simultaneous]: { name: 'Садочные' },
    [WorkplaceType.Continuous]: { name: 'Непрерывные' },
    [WorkplaceType.Kit]: { name: 'Комплектные' },
}

export const WorkplaceTypeList: TypeSpec[] = Object.keys(WorkplaceTypeSpec).map(code => ({
    ...WorkplaceTypeSpec[code as keyof typeof WorkplaceTypeSpec],
    code: code as WorkplaceType,
}));

/**
 * Интерфейс: Директива времени наладки для групп оборудования
 */
export type IWorkplaceAdjustment = {

    /** группа оборудования */
    g: string,

    /** время наладки в секундах */
    t: number,
} & IEntityId

export enum BindingType {
    WorkplaceToOperation = 'Рабочее место',
    OperationToWorkplace = 'Операция',
}

/**
 * Интерфейс: Директива привязки рабочего места и операции
 */
export type IWorkplaceOperationBinding = {

    /** тип привязки */
    t: BindingType,

    /** код операции */
    oId: string,

    /** код рабочего места */
    wId: string,
} & IEntityId

export type WorkModeUnavailablePeriod = {
    /** начало периода */
    s: string,

    /** конец периода */
    e: string,

    /** тип обслуживания (ТО или ремон) */
    type: string,

    /** описание */
    desc?: string,
}

/**
 * Интерфейс: Директива управления режимом работы рабочих мест
 */
export type IWorkMode = {

    /** код рабочего места */
    id: string,

    /** периоды недоступности */
    ex: WorkModeUnavailablePeriod[],
} & IEntityId

/**
 * Интерфейс: Директива ограничения размера партии
 */
export type IBatchSizeLimitation = {

    /** минимальный размер партии */
    m: number,

    /** оптимальный размер партии */
    o: number,
} & IEntityId

/**
 * Интерфейс: Директива минимальный процент использования ВИМ
 */
export type IRouteCodeUsage = {

    /** номера заказов на которые действует директива */
    o: string[],

} & IEntityId

/**
 * Интерфейс: Директива управления количеством производимых материалов в день
 */
export type IVirtualOperations = {

    /** цех.участок */
    ms: string,

    /** стоимость */
    p: number,

    /** производимое количество
     * @deprecated использовать pH
    */
    c: number,

    /** производимость в час */
    pH: number,

    /** группа взаимозаменяемости
     * @deprecated использовать g
    */
    i: string,

    /** группа взаимозаменяемости */
    g: string,

    /** календарь */
    cal: string,
} & IEntityId

/**
 * Интерфейс: Директива управление временем наладки
 */
export type IAdjustmentControl = {

    /** время наладки в процентах */
    tp?: number,

    /** время наладки в секундах */
    ta?: number,

    /** артикул предыдущего ДСЕ */
    d?: string,
} & IEntityId

/**
 * Интерфейс: Директива производительность непрерывных операций
 */
export type IWorkplacePerformance = {

    /** код рабочего места */
    w: string,

    /** производительность */
    p: number,

    /** задержка выгрузки в секундах */
    del?: number,

    /** выходной буфер */
    buf?: number,
} & IEntityId

/**
 * Интерфейс: Директива параллельные операции
 */
export type IParallelOperations = {

    /** группа операций */
    g: string,

    /** коэффициент объема */
    v: number,
} & IEntityId

/**
 * Директива управление прерываниями операций
 */
export type IOperationBreak = {
    /** код операции */
    op: string,
} & IEntityId

/**
 * Интерфейс: Директива процент выполнения операции
 */
export type IPercentExecutionOperations = {
    /** код операции */
    op: string,

    /** процент выполнения */
    pr: number,
} & IEntityId

/**
 * Интерфейс: Материалы с двойными единицами измерения
 */
export type IMaterialMeasureUnits = {
    /** артикул материала */
    material: string,

    /** расчетная е.и. */
    outMu: string,

    /** список входных е.и. */
    inputMu: {
        /** входная е.и. */
        mu: string,
        /** коэфициент конвертации */
        c: number,
    }[],
}